<template>
  <div
    class="currency-input"
    @click="handleInputFocus($event)"
    :class="{ isFocused: isFocused, hasError: errors }"
  >
    <div class="currency-input__container">
      <label :name="name" :for="name" :class="{ disabled: inputIsDisabled }">
        <p class="currency-input__label">{{ label }}</p>
        <input
          class="currency-input__input-field"
          :id="`${name}Input`"
          :placeholder="placeholder"
          v-model.number="inputValue"
          @focus="isFocused = !isFocused"
          @blur="isFocused = !isFocused"
        />
      </label>

      <CurrencyAutocompleteSelect
        :name="props.name"
        :defaultValue="defaultValue[`${name}Type`]"
        :type="'text'"
        :items="items"
        :isLoading="isLoading"
        :isDisabled="autocompleteIsDisabled"
        :autocompleteType="props.name"
        class="dropdown-select"
        @updateAutocomplete="updateSelectedSingle"
      >
        <template #selectItem="{ item }">
          <template v-if="isFiatConfig">
            <span
              :class="`currency-flag currency-flag-${item.toLowerCase()}`"
            ></span>
          </template>

          <CryptoIcon
            v-else
            class="crypto-icon"
            :symbol="`${item.toLowerCase()}`"
            v-show="isCoinConfig"
          />

          <span class="select-list__text">{{ item }}</span>
        </template>
      </CurrencyAutocompleteSelect>

      <p v-if="errors" class="error">{{ errors }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, inject } from "vue";
import { useDebounceFn } from "@vueuse/core";

import { useOrderFormStore } from "@/stores/forms/orderFormStore";

import CryptoIcon from "@/components/icons/CryptoIcon.vue";
import CurrencyAutocompleteSelect from "../selects/CurrencyAutocompleteSelect.vue";

import { isValidNumber } from "@/composables/helpers/validators";

const emit = defineEmits(["dropdownValueUpdate", "inputValueUpdate"]);
const props = defineProps({
  items: { type: Array, required: true },
  name: { type: String, default: "currency-input" },
  label: { type: String, default: "Default label" },
  defaultValue: { type: [Object, String], required: true },
  placeholder: { type: String, required: true },
  type: { type: String, required: true },
  errors: { type: String, default: "" },
  isLoading: { type: Boolean, default: true },
});
const orderStore = useOrderFormStore();
const selectedSingle = ref("");
const isFocused = ref(false);
const isFiatConfig = props.name === "fiat";
const isCoinConfig = props.name === "coin";

const debounceInputValue = useDebounceFn(
  (inputVal) =>
    emit("inputValueUpdate", {
      [`${props.name}Amount`]: inputVal,
    }),
  600
);

const inputValue = computed({
  get() {
    return orderStore.getActiveStore.form[[`${props.name}Amount`]].value;
  },

  set(inputVal) {
    // to trigger validation button to be disabled
    if (!inputVal || isValidNumber(inputVal)) {
      debounceInputValue(inputVal);
    }
  },
});

const handleInputFocus = (e) => {
  if (e.target.className === "currency-input") {
    const childInput = e.target.querySelector("input");
    childInput.focus();
  }
};

const updateSelectedSingle = (selectedItem) => {
  selectedSingle.value = selectedItem;
  emit("dropdownValueUpdate", {
    [`${props.name}Type`]: selectedItem,
  });
};

watch(
  () =>
    props.items &&
    props.defaultValue &&
    props.defaultValue[`${props.name}Type`],
  () => {
    if (props.defaultValue[`${props.name}Type`])
      selectedSingle.value = props.defaultValue[`${props.name}Type`];
  },
  { deep: true }
);

const disableByInputName = inject("isDisabledByKey");
const autocompleteIsDisabled = computed(() => {
  return (
    Object.getOwnPropertyDescriptor(
      disableByInputName.value,
      `${props.name}Type`
    ) && disableByInputName.value[`${props.name}Type`]
  );
});

const inputIsDisabled = computed(() => {
  return (
    Object.getOwnPropertyDescriptor(
      disableByInputName.value,
      `${props.name}Amount`
    ) && disableByInputName.value[`${props.name}Amount`]
  );
});
</script>

<style lang="scss" scoped>
$flag-dimension: 32px;
.currency-input {
  padding: $padding-std;
  border-radius: 1.75rem;
  background: var(--input-background);
  margin-bottom: calc($formFieldSpacing/2);
  position: relative;
  margin-top: 1px; // To accomodate the outline for the focus.

  @media screen and (max-width: $screen-sm) {
    padding: calc($padding-std/2);
  }

  &__container {
    display: grid;
    grid-template-columns: 3fr 0.5fr;
    align-items: center;
    position: relative;
  }

  &__label {
    font-size: calc($font-body/1.1); //1.2rem;
    opacity: 0.6;
  }

  &__input-field {
    margin: 0;
    width: 100%;
    font-size: $font-input-size;
    opacity: 1;
    background: transparent;
    border: transparent;
    outline: none;
    -webkit-appearance: none;
    -moz-appearance: textfield;

    @media screen and (max-width: $screen-sm) {
      font-size: calc($font-sub-jumbo * 0.8);
    }

    @include breakpoint($min-m) {
      font-size: $font-sub-jumbo;
    }

    &:hover,
    &:focus {
      -moz-appearance: textfield;
      -webkit-appearance: none;
    }

    &::-webkit-inner-spin-button {
      -moz-appearance: textfield;
      -webkit-appearance: none;
    }
  }

  &.isFocused {
    outline: 1px solid #0072ce;
  }
  &.hasError {
    outline: 1px solid var(--error-color);
  }
  .error {
    position: absolute;
    bottom: 7px;
    transform: translateY(110%);
    color: var(--error-color);
    font-size: 1.2rem;
  }
}

.currency-input:first-child {
  margin-top: 0.5em;
}

.dropdown-select {
  &__selected-item {
    display: flex;
    align-items: center;

    &__icon {
      margin-inline: 0.75rem;

      &.fiat {
        width: 25px;
        height: 25px;
        border-radius: 50%;
      }

      &.coin {
        width: 25px;
      }
    }
  }
  .crypto-icon {
    display: flex;
    min-width: 25px;
  }
}

.select-list__text {
  padding-left: 0.75rem;
}
</style>
